/**
 * Flow tag ranking — ranks an item among its competitors by an optimizer value.
 * Paths rank within their own group (the default-paths pool, or one rule/
 * conditional group — keyed by parentId); landers/offers rank within their path
 * OR across the flow, per the rule's scope. Pools cover only eligible (active,
 * non-excluded) items and are deduped by itemId so a lander reused on several
 * paths counts once in flow scope.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowTagItem,
  FlowTagRankingMetric,
  FlowTagRankingOp,
  FlowTagRankingScope,
} from "./types";

export function isEligible(item: FlowTagItem): boolean {
  return item.active !== false && !item.excluded && !item.ruleExcluded;
}

export function metricOf(item: FlowTagItem, metric: FlowTagRankingMetric): number {
  const v = item[metric];
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/**
 * The minimal item shape `rankOf` needs to LOCATE a target in a pre-built pool
 * order: only its pool keys (layer + owner path), never the ranked metric. A
 * full {@link FlowTagItem} is a superset, so existing callers pass unchanged;
 * the rules evaluator can hand in a bare `{ layer, itemId, pathId? }`.
 */
export type RankTarget = Pick<
  FlowTagItem,
  "layer" | "itemId" | "pathId" | "parentId"
>;

function ownerPathId(item: RankTarget): string {
  return item.pathId ?? item.parentId ?? "";
}

export interface RankInfo {
  rank: number;
  size: number;
  /**
   * TRUE when this pool is the subset a rule's METRIC conditions admitted,
   * rather than the whole sibling set. It changes what a pool of one means —
   * see the guard in {@link checkRanking}.
   */
  filtered?: boolean;
}

export interface FlowRankIndex {
  rankOf(
    target: RankTarget,
    scope: FlowTagRankingScope,
    metric: FlowTagRankingMetric,
  ): RankInfo | null;
}

interface PoolOrder {
  order: string[];
  /**
   * FALSE when EVERY competitor scores exactly 0 — there is no signal to rank,
   * so the order is pure tie-break (name, then id) and means nothing. The usual
   * cause is a campaign with NO FORMULA GROUPS: `rawScore` sums an empty
   * breakdown, so everything is 0 and "highest"/"lowest" would land on whichever
   * rows sort first and last — alphabetical noise wearing the clothes of a
   * verdict, on the same chips that drive real rotation rules.
   *
   * Deliberately NOT "all values equal": items genuinely tied at a REAL score
   * were measured, and the existing tie-break (name, then id) intentionally
   * mirrors the #N rank badges. Only a total absence of signal is suppressed.
   */
  ranked: boolean;
}

/**
 * Build the deduped, metric-sorted itemId order for one pool. Tie-break by
 * itemName then itemId — identical to the preview rank badges
 * (sortPreviewItemsByRank) so the ⚡ "lowest/highest" tags land on the same
 * row the #N badge shows.
 */
function buildOrder(
  items: FlowTagItem[],
  metric: FlowTagRankingMetric,
): PoolOrder {
  const best = new Map<string, { v: number; name: string }>();
  for (const it of items) {
    const v = metricOf(it, metric);
    const cur = best.get(it.itemId);
    if (!cur || v > cur.v) {
      best.set(it.itemId, { v, name: (it.itemName ?? "").trim().toLowerCase() });
    }
  }
  const values = [...best.values()].map((b) => b.v);
  return {
    order: [...best.entries()]
      .sort(
        ([idA, a], [idB, b]) =>
          b.v - a.v || a.name.localeCompare(b.name) || idA.localeCompare(idB),
      )
      .map(([id]) => id),
    ranked: values.some((v) => v !== 0),
  };
}

export interface RankIndexOptions {
  /** Mark the produced ranks as coming from a metric-FILTERED pool. */
  filtered?: boolean;
}

export function createRankIndex(
  items: ReadonlyArray<FlowTagItem>,
  options: RankIndexOptions = {},
): FlowRankIndex {
  const eligibleByLayer = new Map<string, FlowTagItem[]>();
  for (const it of items) {
    if (!isEligible(it)) continue;
    const list = eligibleByLayer.get(it.layer) ?? [];
    list.push(it);
    eligibleByLayer.set(it.layer, list);
  }
  const cache = new Map<string, PoolOrder>();

  return {
    rankOf(target, scope, metric) {
      // Paths always pool within their own group (parentId, via ownerPathId);
      // landers/offers follow the rule's scope. "path" scope groups by the
      // owner key, which is parentId for paths and pathId for landers/offers.
      const effScope: FlowTagRankingScope =
        target.layer === "paths" ? "path" : scope;
      const path = effScope === "path" ? ownerPathId(target) : "";
      const key = `${target.layer}|${effScope}|${metric}|${path}`;
      let entry = cache.get(key);
      if (!entry) {
        const pool = (eligibleByLayer.get(target.layer) ?? []).filter(
          (it) => effScope === "flow" || ownerPathId(it) === path,
        );
        entry = buildOrder(pool, metric);
        cache.set(key, entry);
      }
      // No signal ⇒ nothing to rank. Same principle as the pool-of-one guard in
      // `checkRanking`: never name a best/worst the data doesn't support.
      if (!entry.ranked) return null;
      const idx = entry.order.indexOf(target.itemId);
      if (idx < 0) return null;
      return {
        rank: idx + 1,
        size: entry.order.length,
        ...(options.filtered ? { filtered: true } : {}),
      };
    },
  };
}

export function checkRanking(
  info: RankInfo,
  op: FlowTagRankingOp,
  percent?: number,
): boolean {
  const { rank, size } = info;
  // A pool of one normally can't be ranked: a lonely item would otherwise match
  // highest AND lowest AND top% AND bottom% at once.
  //
  // The exception is a pool a rule's METRIC conditions deliberately narrowed
  // ("highest raw score AMONG paths with REG >= 202"). There the filter is the
  // statement, and being the only qualifier is a real answer to it — otherwise
  // the tag silently vanishes exactly when the threshold is tuned tightly
  // enough to isolate one element, which is when the author most expects it.
  // Percent ops stay guarded either way: "top 20% of 1" means nothing.
  const lonelyOk =
    info.filtered === true && (op === "highest" || op === "lowest");
  if (size < 2 && !lonelyOk) return false;
  if (op === "highest") return rank === 1;
  if (op === "lowest") return rank === size;
  const p = Math.min(100, Math.max(1, Math.round(percent ?? 0)));
  if (!percent) return false;
  const cutoff = Math.ceil((p / 100) * size);
  return op === "top_percent" ? rank <= cutoff : rank > size - cutoff;
}
